/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import URI from 'jsuri';

import Imgix from 'js/components/Imgix';

import { Button, Grid, Link, PageGridContainer, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import type {
  C4BFieldsFragment,
  ImageWithMetadataFieldsFragment,
} from 'bundles/front-page/__generated__/contentfulTypes';
import { enterprisePartnerLogoDimensions, getEnterprisePartnerLogos } from 'bundles/front-page/constants/defaults';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/front-page';

const styles = {
  showBackground: css`
    background-color: var(--cds-color-grey-25);
  `,
  enterpriseSectionContainer: css`
    color: var(--cds-color-grey-975);
    margin: 0 auto;
    padding: 6rem 0;
    max-width: 1200px;
    ${breakpoints.down('md')} {
      padding: 4rem 0;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-400) 0;
    }
  `,
  gridContainer: css`
    max-width: 100%;
    align-self: center;
    align-items: center;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      padding: 0 var(--cds-spacing-400);
    }

    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,
  logosContainer: css`
    padding: 0 40px;

    ${breakpoints.down('sm')} {
      width: 100%;
      max-width: 575px;
      padding: 0;
      margin-top: var(--cds-spacing-600);
    }
    ${breakpoints.down('md')} {
      margin: 0 auto;
      max-width: 575px;
      width: 100%;
      padding: 0;
    }
  `,
  enterpriseTextAndCtaContainer: css`
    margin-bottom: 35px;
    color: var(--cds-color-grey-975);

    ${breakpoints.down('md')} {
      display: flex;
      margin: 0 auto;
      max-width: 575px;
      width: 100%;
      flex-direction: column;
    }
  `,
  logos: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    background-color: var(--cds-color-grey-50);
    grid-gap: 1px;
  `,
  enterpriseImage: css`
    width: 100%;
    background-color: var(--cds-color-grey-25);
    display: inline-flex;
    height: 100px;
    align-items: center;
    justify-content: center;
    ${breakpoints.down('sm')} {
      height: 80px;
    }
  `,

  textAndCtaHeader: css`
    margin-bottom: var(--cds-spacing-300);
    color: var(--cds-color-grey-975);
  `,
  enterpriseCtaText: css`
    color: var(--cds-color-grey-975);
    margin-bottom: var(--cds-spacing-300);
    max-width: 90%;

    ${breakpoints.down('xs')} {
      max-width: 100%;
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  enterpriseCtaWrapper: css`
    display: flex;
    flex-direction: row;

    ${breakpoints.down('xs')} {
      flex-direction: column;

      a {
        max-width: 100%;
        width: 100%;
      }
    }
  `,
  upskillATeam: css`
    margin-top: var(--cds-spacing-300);
  `,
  teamsLink: css`
    margin-left: var(--cds-spacing-50);
  `,
  mobileEnterpriseTextAndCtaContainer: css`
    padding: var(--cds-spacing-200) var(--cds-spacing-100) 0;
  `,
};

type Props = Omit<C4BFieldsFragment, 'c4BPartnersLogos'> & {
  partnerLogos: ImageWithMetadataFieldsFragment[] | null;
  isUsRegion: boolean;
};

export const getNumberOfDisplayedLogos = (totalLogos: number) => {
  return Math.min(Math.floor(totalLogos / 3) * 3, 9);
};

const renderFallbackLogos = () => {
  const enterpriseLogos = getEnterprisePartnerLogos();
  // Grid should have 3 columns
  const maxDivisibleByThreeOr9 = getNumberOfDisplayedLogos(enterpriseLogos.length);
  return getEnterprisePartnerLogos()
    .slice(0, maxDivisibleByThreeOr9)
    .map((enterprisePartner) => {
      const imageProps = {
        alt: enterprisePartner.title,
        src: enterprisePartner.imgSrc,
        maxWidth: enterprisePartner.maxWidth,
        maxHeight: enterprisePartner.maxHeight,
      };
      return (
        <span css={styles.enterpriseImage} key={enterprisePartner.id} data-testid="ew_banner_logo">
          <Imgix {...imageProps} />
        </span>
      );
    });
};

const renderLogos = (partnerLogos: NonNullable<Props['partnerLogos']>, isUsRegion: boolean) => {
  // Grid should have 3 columns
  const maxDivisibleByThreeOr9 = getNumberOfDisplayedLogos(partnerLogos.length);

  return partnerLogos.slice(0, maxDivisibleByThreeOr9).map(({ image, imageAltText, relationSlug }) => {
    const url = image?.url;

    if (!url) {
      return null;
    }

    const defaultDimensions = enterprisePartnerLogoDimensions.DEFAULT;
    const potentialPartnerDimension =
      isUsRegion && relationSlug && enterprisePartnerLogoDimensions[relationSlug.toUpperCase()];
    const dimensions = potentialPartnerDimension || defaultDimensions;

    const imageProps = {
      alt: imageAltText ? `${imageAltText} Logo` : '',
      src: url,
      ...dimensions,
      maxWidth: '60%',
    };

    return (
      <span css={styles.enterpriseImage} key={imageAltText} data-testid="ew_banner_logo">
        <Imgix {...imageProps} />
      </span>
    );
  });
};

const EnterpriseCtaRebrandNew: React.FunctionComponent<Props> = ({
  partnerLogos,
  isUsRegion,
  c4BTitle,
  c4BText,
  c4BEnterpriseCtaText,
}) => {
  const forEnterpriseLink = new URI('/business')
    .setQuery(
      '?utm_campaign=website&utm_content=banner-from-B2C-home-for-enterprise&utm_medium=coursera&utm_source=enterprise'
    )
    .toString();
  const forTeamsLink = new URI('/business/teams')
    .setQuery(
      '?utm_campaign=website&utm_content=banner-from-B2C-home-for-teams&utm_medium=coursera&utm_source=enterprise'
    )
    .toString();

  const renderedPartnerLogos =
    partnerLogos === null || partnerLogos.length === 0 ? renderFallbackLogos() : renderLogos(partnerLogos, isUsRegion);

  const partnerLogosComponent = (
    <Grid xs={12} sm={6} item css={styles.logosContainer}>
      <div css={styles.logos}>{renderedPartnerLogos}</div>
    </Grid>
  );

  const isMobileDeviceWidth = useMediaQuery(breakpoints.down('xs'));

  return (
    <TrackedDiv
      data-testid="enterprise_banner"
      trackingName="enterprise_cta_view"
      css={styles.showBackground}
      withVisibilityTracking
      trackClicks={false}
    >
      <section css={styles.enterpriseSectionContainer} className="rc-EnterpriseCtaRebrand">
        <PageGridContainer>
          <Grid container justifyContent="center" css={styles.gridContainer}>
            {isMobileDeviceWidth && partnerLogosComponent}
            <Grid
              xs={12}
              sm={6}
              item
              css={
                isMobileDeviceWidth ? styles.mobileEnterpriseTextAndCtaContainer : styles.enterpriseTextAndCtaContainer
              }
            >
              <Typography2
                component="h2"
                css={styles.textAndCtaHeader}
                variant={isMobileDeviceWidth ? 'titleSmall' : 'titleLarge'}
              >
                {c4BTitle}
              </Typography2>

              <Typography2 css={styles.enterpriseCtaText} variant="bodyPrimary" component="p">
                {c4BText}
              </Typography2>
              <div css={styles.enterpriseCtaWrapper}>
                <Button
                  trackingName="coursera_for_enterprise"
                  href={forEnterpriseLink}
                  component={TrackedLink2}
                  target="_blank"
                  variant="primary"
                >
                  {c4BEnterpriseCtaText}
                </Button>
              </div>
              <div css={styles.upskillATeam}>
                <Typography2 component="p">
                  {_t('Upskill a small team?')}
                  <Link
                    css={styles.teamsLink}
                    target="_blank"
                    href={forTeamsLink}
                    variant="standard"
                    typographyVariant="bodyPrimary"
                    icon={<ExternalLinkIcon />}
                    iconPosition="after"
                    component={TrackedLink2}
                    trackingName="coursera_for_teams"
                  >
                    {_t('Check out Coursera for Teams')}
                  </Link>
                </Typography2>
              </div>
            </Grid>
            {!isMobileDeviceWidth && partnerLogosComponent}
          </Grid>
        </PageGridContainer>
      </section>
    </TrackedDiv>
  );
};

export default EnterpriseCtaRebrandNew;
